/**
 * Phase 263 — Student Personal Records & Streaks widget.
 *
 * Sits on the StudentPortal alongside `AcademyChallengeTile`.  Pulls the
 * caller's lifetime challenge metrics from `/api/academy/challenges/me/streaks`
 * and surfaces them as a tight 2-tile + badge-ribbon strip.
 *
 * Honest doctrine:
 *   • If `has_data === false` (zero submissions ever), the widget renders
 *     NOTHING — we don't show "0 streak / 0 wins" trophies, that's a fake
 *     achievement surface.
 *   • Badge tooltips come straight from the backend (no client-side copy
 *     drift).
 *   • All icons are lucide-react; no emoji per codebase convention.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge as ShadBadge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Flame, Trophy, Award, Medal, Sparkles, Loader2, Activity,
  Share2, Download, Square, Smartphone, AlertCircle,
} from 'lucide-react';

const TIER_STYLES = {
  bronze: 'bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-800/60',
  silver: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700',
  gold:   'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800/60',
  elite:  'bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-800 dark:text-fuchsia-200 border-fuchsia-300 dark:border-fuchsia-800/60',
};

const TIER_ICON = {
  bronze: Award,
  silver: Medal,
  gold: Trophy,
  elite: Sparkles,
};

export default function StudentStreakWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await api.get('/academy/challenges/me/streaks');
        if (!cancelled) setData(resp);
      } catch (_) {
        // Silent on this widget — failure to load shouldn't disrupt the page.
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="student-streak-widget-loading">
        <CardContent className="p-4">
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  // ── Honest zero-state — render nothing if the student has no submissions yet.
  if (!data || data.status !== 'ok' || !data.has_data) {
    return null;
  }

  const m = data.metrics || {};
  const badges = data.badges || [];

  return (
    <Card className="border-l-4 border-l-orange-500" data-testid="student-streak-widget">
      <CardContent className="p-4 space-y-3">
        {/* Header — with Share action on the right */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-orange-700 dark:text-orange-300">
            <Activity className="h-3 w-3" />
            Your challenge progression
          </div>
          <ShareStreakDialog metrics={m} />
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-3 gap-2">
          <StreakKpi
            icon={<Flame className="h-4 w-4 text-orange-500" />}
            label="Streak"
            value={m.current_streak_days ?? 0}
            unit={m.current_streak_days === 1 ? 'day' : 'days'}
            testid="streak-kpi-current-streak"
          />
          <StreakKpi
            icon={<Trophy className="h-4 w-4 text-amber-500" />}
            label="Wins"
            value={m.first_place_finishes ?? 0}
            unit={m.first_place_finishes === 1 ? 'win' : 'wins'}
            testid="streak-kpi-first-place"
          />
          <StreakKpi
            icon={<Award className="h-4 w-4 text-slate-500" />}
            label="Submissions"
            value={m.total_challenge_submissions ?? 0}
            unit={m.total_challenge_submissions === 1 ? 'log' : 'logs'}
            testid="streak-kpi-total-submissions"
          />
        </div>

        {/* Badge ribbon */}
        <div className="space-y-1.5" data-testid="streak-badge-ribbon">
          <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
            Earned badges
          </div>
          {badges.length === 0 ? (
            <p className="text-[11px] text-muted-foreground italic">
              Keep submitting scores to unlock badges — your first one is just one log away.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {badges.map((b) => {
                const Icon = TIER_ICON[b.tier] || Award;
                const tone = TIER_STYLES[b.tier] || TIER_STYLES.bronze;
                return (
                  <ShadBadge
                    key={b.id}
                    variant="outline"
                    className={`text-[10px] font-bold cursor-help transition-transform hover:scale-105 ${tone}`}
                    title={b.desc}
                    data-testid={`streak-badge-${b.id}`}
                  >
                    <Icon className="h-2.5 w-2.5 mr-1" />
                    {b.label}
                  </ShadBadge>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StreakKpi({ icon, label, value, unit, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 p-2"
      data-testid={testid}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-slate-500">
        {icon}{label}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className="text-lg font-bold tabular-nums text-slate-900 dark:text-slate-100">
          {value}
        </span>
        <span className="text-[10px] text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Phase 263b — Share Streak Card dialog
// ─────────────────────────────────────────────────────────────────────────────

const FORMAT_META = {
  square: { label: 'Square', sub: '1080 × 1080 — WhatsApp / feed', icon: Square },
  story:  { label: 'Story',  sub: '1080 × 1920 — Stories / Reels', icon: Smartphone },
};

function ShareStreakDialog({ metrics }) {
  const [open, setOpen] = useState(false);
  const [fmt, setFmt] = useState('square');
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);   // { image_base64, filename, width, height }
  const [error, setError] = useState(null);

  const canWebShare = typeof navigator !== 'undefined'
    && typeof navigator.canShare === 'function'
    && typeof navigator.share === 'function';

  // Re-fetch the card whenever the dialog opens OR the format toggles.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setCard(null);
      try {
        const { data } = await api.get(`/academy/challenges/me/share-card?fmt=${fmt}`);
        if (!cancelled) setCard(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err.message || 'Failed to render card.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, fmt]);

  const base64ToBlob = (dataUrl) => {
    const [meta, b64] = dataUrl.split(',');
    const mime = (meta.match(/data:(.*?);base64/) || [])[1] || 'image/png';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  };

  const handleDownload = () => {
    if (!card?.image_base64) return;
    const blob = base64ToBlob(card.image_base64);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = card.filename || 'streak.png';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Card downloaded — share it from your gallery.');
  };

  const handleNativeShare = async () => {
    if (!card?.image_base64) return;
    const blob = base64ToBlob(card.image_base64);
    const file = new File([blob], card.filename || 'streak.png', { type: 'image/png' });
    if (!canWebShare || !navigator.canShare({ files: [file] })) {
      toast.info('Native share not supported on this device — download instead.');
      handleDownload();
      return;
    }
    try {
      await navigator.share({
        files: [file],
        title: `My ${metrics?.current_streak_days || 0}-day challenge streak on Out-Play`,
        text: `Check out my progress on Out-Play! 🔥`,
      });
    } catch (err) {
      // User dismissal returns AbortError — not a real failure.
      if (err?.name !== 'AbortError') {
        toast.error(`Share failed: ${err?.message || 'unknown error'}`);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-[11px] font-bold border-orange-300 dark:border-orange-800 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/30"
        onClick={() => setOpen(true)}
        data-testid="streak-share-button"
      >
        <Share2 className="h-3 w-3 mr-1" />
        Share my streak
      </Button>
      <DialogContent className="sm:max-w-md" data-testid="streak-share-dialog">
        <DialogHeader>
          <DialogTitle>Share your streak</DialogTitle>
          <DialogDescription>
            Generate a branded card and share it to WhatsApp, Instagram Stories,
            or anywhere else.
          </DialogDescription>
        </DialogHeader>

        {/* Format toggle */}
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(FORMAT_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const active = fmt === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFmt(key)}
                className={`flex items-start gap-2 p-2.5 rounded-lg border-2 transition-colors text-left ${
                  active
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
                }`}
                data-testid={`streak-share-format-${key}`}
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${active ? 'text-orange-600' : 'text-slate-400'}`} />
                <div className="space-y-0.5">
                  <p className="text-xs font-bold">{meta.label}</p>
                  <p className="text-[10px] text-muted-foreground">{meta.sub}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Preview */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 overflow-hidden">
          {loading && (
            <div className="aspect-square flex items-center justify-center" data-testid="streak-share-loading">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            </div>
          )}
          {error && (
            <div className="aspect-square flex items-center justify-center p-4 text-center" data-testid="streak-share-error">
              <div className="space-y-2">
                <AlertCircle className="h-5 w-5 text-rose-500 mx-auto" />
                <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>
              </div>
            </div>
          )}
          {card?.image_base64 && !loading && !error && (
            <img
              src={card.image_base64}
              alt="Streak share card preview"
              className={fmt === 'story' ? 'w-full' : 'w-full aspect-square'}
              data-testid="streak-share-preview"
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleDownload}
            disabled={!card || loading}
            data-testid="streak-share-download"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download
          </Button>
          <Button
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
            onClick={handleNativeShare}
            disabled={!card || loading}
            data-testid="streak-share-native"
          >
            <Share2 className="h-3.5 w-3.5 mr-1.5" />
            {canWebShare ? 'Share to apps…' : 'Download (no Web Share)'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
